import { memo, useRef, useEffect, useState } from 'react';
import { addPointsInText } from '../../../../helpers/app';
import { images } from '../../../../constants/images';
import { PublicationPriceIndicatorProps } from '../../interfaces';
import styles from '../../publicationDetail.module.scss';
import InfoOutlineIcon from '@mui/icons-material/InfoOutlined';
import Tooltip from '@mui/material/Tooltip';

const PublicationPriceIndicator = ({
  hasValidSuggestedPrices,
  getPriceCategory,
  getIndicatorPosition,
  getPriceDifferenceMessage,
  publication
}: PublicationPriceIndicatorProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const barRef = useRef<HTMLDivElement>(null);
  const priceRef = useRef<HTMLDivElement>(null);
  const [pricePosition, setPricePosition] = useState<number>(0);

  // Calcular si debe mostrarse
  const shouldShow = publication?.opportunity !== true && hasValidSuggestedPrices;

  useEffect(() => {
    if (!shouldShow) return;

    const calculatePosition = () => {
      if (containerRef.current && barRef.current && priceRef.current) {
        const barWidth = barRef.current.offsetWidth;
        const priceWidth = priceRef.current.offsetWidth;
        const indicatorPosition = getIndicatorPosition();

        const rawPosition = (indicatorPosition / 100) * barWidth;

        const minPosition = priceWidth / 2;
        const maxPosition = barWidth - (priceWidth / 2);

        let boundedPosition = Math.max(minPosition, Math.min(rawPosition, maxPosition));

        const boundedPercentage = (boundedPosition / barWidth) * 100;
        setPricePosition(boundedPercentage);
      }
    };

    calculatePosition();
    window.addEventListener('resize', calculatePosition);
    return () => window.removeEventListener('resize', calculatePosition);
  }, [getIndicatorPosition, hasValidSuggestedPrices, shouldShow]);

  if (!shouldShow) {
    return null;
  }

  return (
    <div className={styles.marketPriceSectionPublication} ref={containerRef}>
      <div className={styles.marketPriceHeader}>
        <h3>Precios del mercado</h3>
        <Tooltip
          title={getPriceDifferenceMessage()}
          placement="top"
          arrow
        >
          <InfoOutlineIcon className={styles.infoIcon} fontSize="medium" />
        </Tooltip>
      </div>

      <div className={styles.priceBarContainer}>
        <div
          className={styles.priceWithIndicator}
          ref={priceRef}
          style={{
            position: 'absolute',
            left: `${pricePosition}%`,
            transform: 'translateX(-50%)',
            top: '-50px',
            transition: 'left 0.3s ease',
            alignItems: 'center'
          }}
        >
          <span className={styles.currentPriceAmount}>${addPointsInText(publication?.price)}</span>
          {(getPriceCategory() === "good" || getPriceCategory() === "fair") && (
            <div className={styles.priceCategory}>
              <img
                src={
                  getPriceCategory() === "good"
                    ? images.checkVerde
                    : images.checkAmarillo
                }
                alt="Indicador de precio"
                className={styles.priceIcon}
              />
              <span>
                {getPriceCategory() === "good"
                  ? "Buen precio"
                  : "Precio justo"}
              </span>
            </div>
          )}
        </div>

        <div className={styles.priceBar} ref={barRef}>
          <div className={styles.pricePointer} style={{ left: `${getIndicatorPosition()}%` }} />
          <div className={styles.centerPointer} />
        </div>
      </div>

      <div className={styles.suggestedPrice}>
        <span className={styles.suggestedPriceAmount}>
          ${addPointsInText(publication?.vehicle_data?.suggested_price?.suggested_price || publication?.suggested_prices?.sale)}
        </span>
        <span className={styles.suggestedPriceLabel}>Precio promedio</span>
      </div>
    </div>
  );
};

export default memo(PublicationPriceIndicator);
